import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import useSmallScreen from '../../../hooks/useSmallScreen';
import ColumnAvatar from '../../../components/Redesing/column-avatar';
import { useMemo, useState } from 'react';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';

import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  useOrganizationsRelayQuery,
  mapRelayOrganizationsToRows,
  IOrganizationRow,
  OrganizationSortInput,
} from '../../../hooks/organization/query/V2';
import { useOrganizationStore } from '../../../store/organizationStore';
import { history } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import useOrganization from '../../../hooks/use-organization';

function OrganizationTable({
  pageState,
  handleSearch,
  onClose,
  clearSystemData,
  hasAccess,
}: {
  pageState: {
    search: string;
    selectedFilter: { label: string; value: string }[];
    filter: { label: string; value: string }[];
  };
  handleSearch: (search: string) => void;
  onClose: () => void;
  clearSystemData: () => void;
  hasAccess: boolean;
}) {
  const isSmallScreen = useSmallScreen();

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pageNumber, setPageNumber] = useState(0);
  const [sort, setSort] = useState<ISortConfig>({
    key: 'name',
    direction: 'asc',
  });

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedOrganizations, setAccumulatedOrganizations] = useState<
    IOrganizationRow[]
  >([]);

  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  // get organization store info from zustand
  const organizationInfo = useOrganization();

  const organizationsFilter = useMemo(
    () => (debouncedSearchTerm ? { search: debouncedSearchTerm } : undefined),
    [debouncedSearchTerm]
  );

  const graphqlSort: OrganizationSortInput[] = useMemo(
    () => [
      {
        field: 'NAME',
        order: sort.direction === 'asc' ? 'ASC' : 'DESC',
      },
    ],
    [sort]
  );

  const {
    data: graphqlData,
    count: totalOrganizations,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading,
    isRefetching,
  } = useOrganizationsRelayQuery(organizationsFilter, {
    limit: 100,
    after: afterCursor,
    sort: graphqlSort,
  });

  const organizationRows = useMemo(
    () => (graphqlData ? mapRelayOrganizationsToRows(graphqlData) : []),
    [graphqlData]
  );

  useMemo(() => {
    if (!organizationRows.length && !afterCursor) {
      setAccumulatedOrganizations([]);
      return;
    }
    if (organizationRows.length) {
      if (afterCursor) {
        setAccumulatedOrganizations((prev) => [...prev, ...organizationRows]);
      } else {
        setAccumulatedOrganizations(organizationRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationRows]);

  const organizations =
    accumulatedOrganizations.length > 0 ? accumulatedOrganizations : organizationRows;

  const resetPagination = () => {
    setAccumulatedOrganizations([]);
    setAfterCursor(null);
    setPageNumber(0);
  };

  const handleSetOrganizationId = (organizationId: string) => {
    const setOrganizationInfo = useOrganizationStore.getState().setOrganizationInfo;

    if (organizationInfo?.id === organizationId) return;

    // get organization name
    const organizationName = organizations.find(
      (organization: IOrganizationRow) => organization.id === organizationId
    )?.name;

    setOrganizationInfo({ id: organizationId, name: organizationName, access: -1 });
    localStorage.setItem('organizationId', organizationId);
    clearSystemData();
    history.push('/dashboard');
    onClose();
  };

  return (
    <>
      <PaginationTable
        padding={{
          pt: 1,
          pl: 0,
          pr: 1,
        }}
        totalItems={totalOrganizations}
        hasMore={graphqlHasMore || organizations.length > (pageNumber + 1) * rowsPerPage}
        isLoading={
          isLoading ||
          (pageNumber > 0 && organizations.length <= pageNumber * rowsPerPage)
        }
        isLoadingCache={isRefetching}
        customKey="id"
        onRowCLick={(id: string) => handleSetOrganizationId(id)}
        handleSearch={(search) => {
          resetPagination();
          handleSearch(search.target.value);
        }}
        searchText={pageState.search}
        sortBy={sort.key}
        sortDirection={sort.direction}
        onSortChange={(sort) => {
          resetPagination();
          setSort(sort as ISortConfig);
        }}
        dataId="org"
        showFilter={false}
        buttons={[
          {
            startIcon: <AddIcon />,
            variant: 'contained',
            label: 'Add',
            dataId: 'add-organization',
            disabled: !hasAccess,
            onClick: () => {
              history.push('/addOrganization');
              onClose();
            },
          },
        ]}
        columns={
          [
            ...(isSmallScreen
              ? [
                  {
                    id: 'name',
                    label: 'Organization Name',
                    searchable: true,
                    maxWidth: 'none',
                    format: (row: IOrganizationRow) => {
                      return (
                        <ColumnAvatar
                          noEllipsis
                          avatar={<CorporateFareIcon />}
                          firstRow={row.name}
                        />
                      );
                    },
                  },
                ]
              : [
                  {
                    id: 'name',
                    label: 'Organization Name',
                    searchable: true,
                    showToolTip: true,
                    format: (row: IOrganizationRow) => {
                      return (
                        <ColumnAvatar
                          noEllipsis
                          avatar={<CorporateFareIcon />}
                          firstRow={row.name}
                        />
                      );
                    },
                  },
                ]),
          ] as IColumn[]
        }
        localSearch={false}
        items={organizations || []}
        onPageChange={(page, newPage) => {
          const incrementedPage = newPage + 1;
          if (
            organizations?.length &&
            organizations.length / rowsPerPage < incrementedPage
          ) {
            setAfterCursor(endCursor || null);
          }
          setPageNumber(newPage);
        }}
        onRowsPerPageChange={(pageSizeString) => {
          const pageSize = Number(pageSizeString.target.value);
          resetPagination();
          setRowsPerPage(pageSize);
        }}
        page={pageNumber}
        rowsPerPage={rowsPerPage}
      />
    </>
  );
}

export default OrganizationTable;
